import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc,
  query, orderBy, onSnapshot, runTransaction, serverTimestamp, setDoc,
} from 'firebase/firestore';
import { db } from './firebase';

const DOCS_COL  = 'bizops_documents';
const CTR_COL   = 'bizops_doc_counters';

// ─── Document types ───────────────────────────────────────────────────────────
export const DOC_TYPES = {
  QT: { label: 'ใบเสนอราคา',        labelEn: 'Quotation',             prefix: 'QT' },
  IV: { label: 'ใบแจ้งหนี้/ใบวางบิล', labelEn: 'Invoice',               prefix: 'IV' },
  RC: { label: 'ใบเสร็จรับเงิน',     labelEn: 'Receipt',               prefix: 'RC' },
  TI: { label: 'ใบกำกับภาษี',        labelEn: 'Tax Invoice',           prefix: 'TI' },
  WT: { label: 'ใบหัก ณ ที่จ่าย',    labelEn: 'Withholding Tax Cert.', prefix: 'WT' },
  WC: { label: 'หนังสือรับรองหัก ณ ที่จ่าย', labelEn: 'WHT Certificate (Sec.50 bis)', prefix: 'WC' },
};

export const DOC_STATUSES = {
  draft:     { label: 'ร่าง',         color: '#6b7280', bg: 'rgba(107,114,128,.15)' },
  sent:      { label: 'ส่งแล้ว',      color: '#3b6ef5', bg: 'rgba(59,110,245,.15)'  },
  paid:      { label: 'ชำระแล้ว',     color: '#16a34a', bg: 'rgba(22,163,74,.15)'   },
  void:      { label: 'ยกเลิก',       color: '#dc2626', bg: 'rgba(220,38,38,.15)'   },
};

// ─── Running number (atomic) ──────────────────────────────────────────────────
// Counter doc id = "QT-2026", "IV-2026", etc.
export async function getNextDocNumber(type) {
  const year   = new Date().getFullYear();
  const ctrId  = `${type}-${year}`;
  const ctrRef = doc(db, CTR_COL, ctrId);

  let nextNum;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ctrRef);
    nextNum = (snap.exists() ? snap.data().last_number : 0) + 1;
    tx.set(ctrRef, { type, year, last_number: nextNum }, { merge: true });
  });

  const padded = String(nextNum).padStart(4, '0');
  return `${type}-${year}-${padded}`;   // e.g. QT-2026-0001
}

// ─── COMPANY INFO (issuer) ────────────────────────────────────────────────────
export const COMPANY = {
  name:    'บริษัท แฟลชยัวร์มีม จำกัด',
  nameEn:  'FLASHYOURMEME CO., LTD.',
  tax_id:  '0145565003261',
  address: '628/164 ถนนเลียบคลองรังสิต ตำบลประชาธิปัตย์ อำเภอธัญบุรี จ.ปทุมธานี 12130',
  phone:   '086-424-6635',
  email:   '',
  // บัญชีธนาคารสำหรับรับชำระเงิน
  bank_accounts: [
    { bank: 'ธนาคารกสิกรไทย', branch: 'สาขาฟิวเจอร์พาร์ค รังสิต', account_name: 'บริษัท แฟลชยัวร์มีม จำกัด', account_number: '123-4-56789-0', account_type: 'ออมทรัพย์' },
    { bank: 'ธนาคารกสิกรไทย', branch: '', account_name: 'น.ส. ปราณปัทม์ ปฏิพัตร์ และ น.ส. ชัชชญา วงษ์ศิริ', account_number: '110-3-88270-9', account_type: 'ออมทรัพย์' },
  ],
};

// ─── Company Settings (bank accounts etc.) ────────────────────────────────────
const SETTINGS_COL = 'bizops_settings';
const SETTINGS_DOC = 'company';

export async function getCompanySettings() {
  const snap = await getDoc(doc(db, SETTINGS_COL, SETTINGS_DOC));
  if (!snap.exists()) return { bank_accounts: COMPANY.bank_accounts };
  return { bank_accounts: COMPANY.bank_accounts, ...snap.data() };
}

export async function saveCompanySettings(data) {
  const ref = doc(db, SETTINGS_COL, SETTINGS_DOC);
  await setDoc(ref, data, { merge: true });
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────
export async function createDocument(data) {
  const doc_number = await getNextDocNumber(data.doc_type);
  const payload = {
    ...data,
    doc_number,
    status: data.status || 'draft',
    issuer: COMPANY,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, DOCS_COL), payload);
  return { id: ref.id, ...payload };
}

export async function updateDocument(id, data) {
  const ref = doc(db, DOCS_COL, id);
  await updateDoc(ref, { ...data, updated_at: serverTimestamp() });
}

export async function deleteDocument(id) {
  await deleteDoc(doc(db, DOCS_COL, id));
}

export async function getDocument(id) {
  const snap = await getDoc(doc(db, DOCS_COL, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function getAllDocuments() {
  const snap = await getDocs(
    query(collection(db, DOCS_COL), orderBy('created_at', 'desc'))
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Real-time subscription — newest first
export function subscribeDocuments(callback) {
  const q = query(collection(db, DOCS_COL), orderBy('created_at', 'desc'));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// price_mode: 'exclusive' = ราคาไม่รวม VAT (แยกภาษี)
//             'inclusive' = ราคารวม VAT แล้ว (รวมภาษี)
// vat_type per item: 'vat7' | 'zero' | 'exempt'
// discount_type per item: 'percent' | 'amount'
export function calcTotals({ items = [], wht_rate = 0, price_mode = 'exclusive' }) {
  let subtotal_before_discount = 0;
  let total_discount           = 0;
  let subtotal_vatable         = 0; // net after discount, vat7 items
  let subtotal_exempt          = 0; // net after discount, zero/exempt items

  items.forEach(it => {
    const qty        = Number(it.qty)        || 0;
    const unit_price = Number(it.unit_price) || 0;
    const line_gross = qty * unit_price;

    let disc = 0;
    if (it.discount_type === 'percent') {
      disc = line_gross * (Number(it.discount_value) || 0) / 100;
    } else {
      disc = Number(it.discount_value) || 0;
    }
    disc = Math.min(Math.max(disc, 0), line_gross);

    const line_net = line_gross - disc;
    subtotal_before_discount += line_gross;
    total_discount           += disc;

    if (it.vat_type === 'vat7') subtotal_vatable += line_net;
    else                         subtotal_exempt  += line_net;
  });

  let vat_amount          = 0;
  let subtotal_vatable_excl = subtotal_vatable; // excl. VAT base

  if (price_mode === 'inclusive') {
    // ราคารวม VAT → ถอย VAT ออก
    vat_amount            = Math.round(subtotal_vatable * 7 / 107 * 100) / 100;
    subtotal_vatable_excl = Math.round((subtotal_vatable - vat_amount) * 100) / 100;
  } else {
    // ราคาแยก VAT → บวก VAT เพิ่ม
    vat_amount = Math.round(subtotal_vatable * 0.07 * 100) / 100;
  }

  const subtotal   = subtotal_vatable_excl + subtotal_exempt;
  const wht_amount = wht_rate > 0 ? Math.round(subtotal * (wht_rate / 100) * 100) / 100 : 0;
  const total      = subtotal + vat_amount - wht_amount;

  return {
    subtotal_before_discount: Math.round(subtotal_before_discount * 100) / 100,
    total_discount:           Math.round(total_discount           * 100) / 100,
    subtotal_vatable,
    subtotal_vatable_excl,
    subtotal_exempt:          Math.round(subtotal_exempt          * 100) / 100,
    subtotal:                 Math.round(subtotal                 * 100) / 100,
    vat_amount,
    wht_amount,
    total:                    Math.round(total                    * 100) / 100,
  };
}
